/* eslint-disable eqeqeq */
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import { Card, Grid, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import { CollapseIcon, ComputerCPUIcon, ComputerGPUIcon, ComputerHDDIcon, ComputerKeyboardIcon, ComputerLaptopIcon, ComputerRAMIcon, ExpandIcon, OilGasMeterIcon } from "component/commonComponents/Icon";
import GeneralDialog from "component/dialogComponents/generalDialog/generalDialog";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Slider } from "component/commonComponents/Slider";
import { ThemeContext } from 'styled-components';
import Typography from "../../commonComponents/Typography";
import UptimeButton from '../../commonComponents/button/button';
import EyeBrow from "../../commonComponents/eyeBrow/eyeBrow";
import { defaultSliderSettings } from "./defaultData";
import { StyledDeviceSelection } from "./deviceSelection.style";
import { SourceBox } from './dragndrop/sourceBox';
import { TargetBox } from "./dragndrop/targetBox";
import CurrentDevice from './currentDevice/currentDevice';

export interface IDeviceSelectionProps {
  yourDevice?: any,
  availableDevices?: any,
  sliderSetting?: any,
  handleSelection?: any,
  handleCompare?: any,
  handleExpandCollapse?:any,
  selectedDevice?: any,
  comparedDevices?: any,
  description?: string,
  locale?: any,
  updateListDevicesChangeKeyBoard?: any;
  isPickup?: boolean;
  eyeBrowColor: (props: any) => void;
  eyeBrowBackgroundColor: (props: any) => void;
};

const DeviceSelection: React.FC<IDeviceSelectionProps> = (props): JSX.Element => {
  const { availableDevices, sliderSetting, handleSelection, handleCompare, handleExpandCollapse, selectedDevice, comparedDevices, description, locale, updateListDevicesChangeKeyBoard, isPickup, yourDevice, eyeBrowColor, eyeBrowBackgroundColor } = props;
  const [comparingDevices, setComparingDevices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmedDevice, setConfirmedDevice] = useState<any>(null);
  const [expandSpec, setExpandSpec] = useState(false);
  const optionLength = 3;
  const maxLength = 50;
  let selectDeviceComponentText : any = null;
  let commonStatusText : any = null;
  if(locale){
    const {selectDeviceComponent, commonStatus} = locale;
    selectDeviceComponentText = selectDeviceComponent;
    commonStatusText = commonStatus;
  }
  const theme = useContext(ThemeContext);
  const smUp = useMediaQuery(`(min-width:${theme.breakpoints.sm})`);
  const [listDevices, setListDevices] = useState(availableDevices?.data || []);
  const ref = useRef<any>({});

  const customSliderSetting = {
    onInit: ()=>{
      if (ref.current?.innerSlider?.list) {
        const slides = ref.current.innerSlider.list.querySelectorAll('.slick-slide');
        slides.forEach((slide:any) => {
          slide.setAttribute('tabindex', '0');
          slide.removeAttribute('aria-hidden');
        });
      }
    },
    onReInit: ()=>{
      if (ref.current?.innerSlider?.list) {
        const slides = ref.current.innerSlider.list.querySelectorAll('.slick-slide');
        slides.forEach((slide:any) => {
          slide.setAttribute('tabindex', '0');
          slide.removeAttribute('aria-hidden');
        });
      }
    },
    accessibility: true,
  };

  const listKeyChange = useMemo(() => {
    if (!Array.isArray(availableDevices?.data)) return '';
    return availableDevices.data.reduce((listKey: string, item: any) => listKey + item.uid + (item?.exception_type?.text || ''), '');
  }, [availableDevices?.data]);

  useEffect(() => {
    setListDevices(availableDevices?.data);
  }, [listKeyChange]);

  useEffect(() => {
    const newComparedDevices = comparedDevices && comparedDevices.length > 0 ? comparedDevices.map((i: any) => {
      return listDevices.find((d: any) => d.uid === i.uid) ?? null;
    })?.filter(Boolean) : [];
    setComparingDevices(newComparedDevices);
    handleCompare(newComparedDevices);
    const newSelectedDevice = listDevices.find((d: any) => d.uid === selectedDevice?.uid) ?? null;
    setConfirmedDevice(newSelectedDevice);
    handleSelection(newSelectedDevice);
  }, [selectedDevice, JSON.stringify(comparedDevices), listDevices]);

  const handleChangeKeyBoard = (uid: string, value: string) => {
    const index = listDevices.findIndex((item: any) => item.uid === uid);
    listDevices[index].keyboardSelected = value || '';
    setListDevices([...listDevices]);
    updateListDevicesChangeKeyBoard([...listDevices]);
  };

  const updateComparingDevices = (item: any) => {
    if (!item) {
      return;
    }
    const itemUid = item.uid;
    if (isDisabled(itemUid)) {
      return;
    }
    const isComparing = comparingDevices.findIndex((i: any) => i.uid === itemUid) !== -1;
    if (isComparing) {
      if (confirmedDevice && confirmedDevice.uid === itemUid && !dialogOpen) {
        setDialogOpen(true);
      } else {
        const filtered = comparingDevices.filter((i: any) => i.uid != itemUid);
        setComparingDevices(filtered);
        handleCompare(filtered);
      }
    } else {
      const newItem = listDevices.find((i: any) => i.uid === itemUid);
      const newCompares: any = [...comparingDevices, newItem];
      setComparingDevices(newCompares);
      handleCompare(newCompares);
    }
  }

  const removeSelectedDevice = () => {
    setDialogOpen(false);
    const item = JSON.parse(JSON.stringify(confirmedDevice));
    updateConfirmedDevice(null);
    updateComparingDevices(item);
  }

  const updateConfirmedDevice = (item: any) => {
    setConfirmedDevice(item);
    handleSelection(item);
  }

  const expandCollapse = () => {
    setExpandSpec(!expandSpec);
    handleExpandCollapse();
  }

  const exception_type = {
    PREMIUM: "Premium",
    STANDARD: "Standard",
    RECOMMENDED: 'RECOMMENDED',
    NO_EXCEPTION_REQUIRED: 'NO_EXCEPTION_REQUIRED',
    EXCEPTION_REQUIRED: 'EXCEPTION_REQUIRED',
    NA: 'NA',
  };

  const FIELD_TO_GET_NAME = {
    TYPE: 'type',
    EXCEPTION_TYPE: 'exception_type'
  };

  const exceptionLabel = (item: any, field: string) => {
    if (!item || !item[field]) {
      return '';
    }

    switch (isPickup ? item[field] : item[field].value) {
      case exception_type.RECOMMENDED:
      case exception_type.EXCEPTION_REQUIRED:
      case exception_type.PREMIUM:
      case exception_type.STANDARD:
        if (isPickup) { 
          return commonStatusText?.[item[field]?.toUpperCase()];
        } else {
          return commonStatusText?.[item[field]?.value?.toUpperCase()];
        }
      default:
        return '';
    }
  }

  const isDisabled = (id: any) => {
    return (comparingDevices.findIndex((i: any) => i.uid == id) === -1) && (comparingDevices.length === optionLength) ;
  };

  const isChecked = (id: any) => {
    return comparingDevices.findIndex((i: any) => i.uid == id) !== -1;
  };

  const canDrag = (id: any) => {
    return comparingDevices.length < optionLength && comparingDevices.findIndex((i: any) => i.uid == id) === -1;
  };

  const buttonSubmit = (
    <UptimeButton
      label={selectDeviceComponentText?.deleteDialog.button.proceed}
      endIcon={<ArrowForwardIosIcon />}
      onClick={() => removeSelectedDevice()}
    />
  );

  const content = (
    <Typography>{selectDeviceComponentText?.deleteDialog.content}</Typography>
  );

  const renderEyeBrow = (item: any) => {
    const eyeBrowData = {
      item,
      field: isPickup ? FIELD_TO_GET_NAME.TYPE : FIELD_TO_GET_NAME.EXCEPTION_TYPE,
      isPickup,
      theme,
    };
    return (
      exceptionLabel(item, isPickup ? FIELD_TO_GET_NAME.TYPE : FIELD_TO_GET_NAME.EXCEPTION_TYPE) ?
        (<EyeBrow
          color={eyeBrowColor({...eyeBrowData})}
          backgroundColor={eyeBrowBackgroundColor({...eyeBrowData})}
          text={exceptionLabel(item, isPickup ? FIELD_TO_GET_NAME.TYPE : FIELD_TO_GET_NAME.EXCEPTION_TYPE)}
        />)
      :
        <></>
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <StyledDeviceSelection isMobileView={smUp} elevation={0}>
        <Grid>
          {yourDevice.currentDevice && (
            <Grid style={{ padding: "20px" }}>
              <CurrentDevice
                device={yourDevice}
                title={selectDeviceComponentText.yourCurrentDevice}
                isSpecification={true}
                isBackground={true}
                locale={locale.currentDevice}
                isPickup={isPickup}
              />
            </Grid>
          )}

          <div className="slider-container">
            <Typography className="device-slider-header">
              <strong>{selectDeviceComponentText?.selectToCompare}</strong> 
              <small>({selectDeviceComponentText?.maxinumOptionLength.replace(/\{{.*\}}/g, optionLength)})</small>
            </Typography>

            <Slider 
              className="slider device-slider" 
              {...defaultSliderSettings}
              {...customSliderSetting}
              {...sliderSetting}
              ref={ref}
            >
              {listDevices.map((item: any, index: number) =>
                <SourceBox
                  item={item}
                  key={item.uid || index}
                  onChange={updateComparingDevices}
                  handleChangeKeyBoard={handleChangeKeyBoard}
                  isChecked={isChecked}
                  canDrag={canDrag}
                  locale={locale}
                  renderEyeBrow={() => renderEyeBrow(item)}
                  isPickup={isPickup}
                />
              )}
            </Slider>
          </div>

          <Typography className="stepper-label">{description}</Typography>

          <Grid container justifyContent="center" className="grid-comparison" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'center', sm: 'unset' }}>
            {comparingDevices.map((item: any, index: any) =>
              <Card 
                key={index}
                id='crd-deviceselection-container'
                className={`comparison-card-container ${confirmedDevice && confirmedDevice.uid === item.uid ? 'selected' : ''}`}
              >
                {renderEyeBrow(item)}

                <IconButton onClick={() => updateComparingDevices(item)} className="close-button" id="btn-deviceselection-close" aria-label="Close Device Selection">
                  <CloseIcon style={{ fontSize: 15 }} />
                </IconButton>

                <Grid container direction="column">
                  <Grid container direction="column" alignItems="center" className="image-container">
                    <img src={item?.model_picture || item?.picture} alt={item.model} className="device-image" id="img-deviceselection-model_picture" title={item.model}/>
                  </Grid>

                  <Typography className="manufacturer" id="lbl-deviceselection-manufacturer">
                    {item.manufacturer}
                  </Typography>

                  <Tooltip title={item?.name || item?.user_device_profile} arrow placement="bottom">
                    <Typography className="title" id="lbl-deviceselection-title" display="block !important" maxWidth="100% !important">
                      {item?.name || item?.user_device_profile}
                    </Typography>
                  </Tooltip>

                  <Grid container direction="column" className="specification-container">
                    <Typography className="specification" id="lbl-deviceselection-specification">{selectDeviceComponentText?.specification}:</Typography>

                    <div className="specification-content">
                      <div className="spec-details">
                        <Tooltip title={selectDeviceComponentText?.tooltip.processor} arrow placement="left">
                          <ComputerCPUIcon
                            role="img"
                            aria-label={selectDeviceComponentText?.tooltip.cpu}
                            className="spec-icon"
                          />
                        </Tooltip>

                        <Tooltip title={`${item.processor?.length > maxLength ? item.processor : ''}`} arrow placement="top-start">
                          <div className="spec-value" id="lbl-deviceselection-processor">{item.processor}</div>
                        </Tooltip>
                      </div>

                      <div className="spec-details">
                        <Tooltip title={selectDeviceComponentText?.tooltip.graphicsProcessor} arrow placement="left">
                          <ComputerGPUIcon
                            role="img"
                            aria-label={selectDeviceComponentText?.tooltip.gpu}
                            className="spec-icon"
                          />
                        </Tooltip>

                        <Tooltip title={`${item.graphics_processor?.length > maxLength ? item.graphics_processor : ''}`} arrow placement="top-start">
                          <div className="spec-value" id="lbl-deviceselection-graphics_processor">{item.graphics_processor}</div>
                        </Tooltip>
                      </div>

                      <div className="spec-details">
                        <Tooltip title={selectDeviceComponentText?.tooltip.memory} arrow placement="left">
                          <ComputerRAMIcon
                            role="img"
                            aria-label={selectDeviceComponentText?.tooltip.ram}
                            className="spec-icon"
                          />
                        </Tooltip>

                        <Tooltip title={`${item.memory?.length > maxLength ? item.memory : ''}`} arrow placement="top-start">
                          <div className="spec-value" id="lbl-deviceselection-memory">{item.memory}</div>
                        </Tooltip>
                      </div>

                      <div className="spec-details">
                        <Tooltip title={selectDeviceComponentText?.tooltip.hardDrive} arrow placement="left">
                          <ComputerHDDIcon
                            role="img"
                            aria-label={selectDeviceComponentText?.tooltip.hdd}
                            className="spec-icon"
                          />
                        </Tooltip>

                        <Tooltip title={`${item.hard_drive?.length > maxLength ? item.hard_drive : ''}`} arrow placement="top-start">
                          <div className="spec-value" id="lbl-deviceselection-hard_drive">{item.hard_drive}</div>
                        </Tooltip>
                      </div>

                      {expandSpec && (<div className="spec-details">
                        <Tooltip title={selectDeviceComponentText?.tooltip.display} arrow placement="left">
                          <ComputerLaptopIcon
                            role="img"
                            aria-label={selectDeviceComponentText?.tooltip.screen}
                            className="spec-icon"
                          />
                        </Tooltip>

                        <Tooltip title={`${item.display?.length > maxLength ? item.display : ''}`} arrow placement="top-start">
                          <div className="spec-value" id="lbl-deviceselection-size">{item.display}</div>
                        </Tooltip>
                      </div>)}

                      {expandSpec && (<div className="spec-details">
                        <Tooltip title={selectDeviceComponentText?.tooltip.weight} arrow placement="left">
                          <OilGasMeterIcon
                            role="img"
                            aria-label={selectDeviceComponentText?.tooltip.weight}
                            className="spec-icon"
                          />
                        </Tooltip>
                        <div className="spec-value" id="lbl-deviceselection-weight">{item.weight}</div>
                      </div>)}

                      {expandSpec && (<div className="spec-details">
                        <Tooltip title={selectDeviceComponentText?.tooltip.keyboardLayout} arrow placement="left">
                          <ComputerKeyboardIcon
                            role="img"
                            aria-label={selectDeviceComponentText?.tooltip.keyboardLayout}
                            className="spec-icon"
                          />
                        </Tooltip>
                        <div className="spec-value" id={`lbl-deviceselection-${isPickup ? 'name' : 'language'}`}>
                          {isPickup ?
                            item.keyboards?.find(
                              (k: { code: string }) =>
                                k.code === item.keyboardSelected
                            )?.name
                            :
                            item.language
                          }
                        </div>
                      </div>)}
                    </div>
                  </Grid>

                  <Grid container direction="column" alignItems="center" className="actions">
                    <UptimeButton
                      variant={confirmedDevice && confirmedDevice.uid === item.uid ? 'contained' : 'outlined'}
                      label={confirmedDevice && confirmedDevice.uid === item.uid ? selectDeviceComponentText?.selected : selectDeviceComponentText?.select}
                      onClick={() => updateConfirmedDevice(item)}
                      id="btn-select-device"
                      data-model_uid={item.uid}
                    />

                    <Tooltip title={expandSpec ? selectDeviceComponentText?.collapse : selectDeviceComponentText?.expand} arrow placement="left">
                      {
                        expandSpec ? (
                          <CollapseIcon alt={selectDeviceComponentText?.collapse} className="expand-collapse" onClick={() => expandCollapse()} id="icon-deviceselection-collapse" />
                        ) : (
                          <ExpandIcon alt={selectDeviceComponentText?.expand} className="expand-collapse" onClick={() => expandCollapse()} id="icon-deviceselection-expand" />
                        )
                      }
                    </Tooltip>
                  </Grid>
                </Grid>
              </Card>
            )}

            {Array(optionLength - comparingDevices.length).fill(null).map((value, index) =>
              <TargetBox key={index} optionNumber={comparingDevices.length + index + 1} locale={locale}/>
            )}

          </Grid>
        </Grid>
        
        <GeneralDialog
          title={selectDeviceComponentText?.deleteDialog.title}
          content={content}
          isOpen={dialogOpen}
          handleClose={() => setDialogOpen(false)}
          buttonSubmit={buttonSubmit}
          deviders={false}
          headerBackgroundDefault={false}
          cancelLabel={selectDeviceComponentText?.deleteDialog.button.cancel}
          classname='delete-device-dialog'
        />
      </StyledDeviceSelection>
    </DndProvider>
  );
};

DeviceSelection.defaultProps = {
  yourDevice: {},
  availableDevices: {
    data: [],
  },
  handleSelection: () => console.log('Device selected'),
  handleCompare: () => {},
  handleExpandCollapse: () => {},
  sliderSetting: {},
  locale: {
    selectDeviceComponent: {
      recommended: "Recommended",
      yourCurrentDevice: "My Current Device",
      option_1: "Option 1",
      option_2: "Option 2",
      description: "Description",
      selected: "SELECTED",
      select: "SELECT",
      sliderHeader: {
          header_1: "Maximum",
          header_2: "items for comparison"
      },
      collapse: "Collapse",
      expand: "Expand",
      selectToCompare: "Select To Compare",
      maxinumOptionLength: "Maximum {optionLength} items for comparison",
      specification: "Specification"
    }
  },
  description: "Click or drag to compare & select your preferred model",
};

export default DeviceSelection;
